import api, { queryClient } from "@app/config/api/api";
import type { components, operations } from "@generated/openapi";
import { useSuspenseQuery } from "@tanstack/react-query";

export type ResearchSession = components["schemas"]["ResearchSession"];
export type ResearchSessionStatus = ResearchSession["status"];
export type ResearchReportListItem = components["schemas"]["ResearchReportListItem"];
export type ResearchReport = components["schemas"]["ResearchReport"];
export type ListResearchReportsQuery = NonNullable<operations["list_research_reports"]["parameters"]["query"]>;

// experiment_id is a required query param on the sessions list endpoint, so api.queryOptions can't
// build a paramless key here (unlike /datasets/ or /experiments/); invalidate by the [method, path]
// prefix react-query matches against instead, covering every experiment_id variant in the cache.
export const invalidateResearchSessions = () =>
  queryClient.invalidateQueries({ queryKey: ["get", "/research/sessions/"] });

export const invalidateResearchReports = () =>
  queryClient.invalidateQueries({ queryKey: ["get", "/research/reports/"] });

// throwOnError:false so a failed fetch renders an inline Alert in the Research tab rather than
// escaping to the route error boundary (the queryClient default throws); mirrors the agent-session
// messages query, which opts out for the same reason.
export const useResearchSessions = (experimentId: string, enabled: boolean = true) =>
  api.useQuery(
    "get",
    "/research/sessions/",
    { params: { query: { experiment_id: experimentId } } },
    { enabled, throwOnError: false }
  );

export const useResearchReports = (query: ListResearchReportsQuery = {}, enabled: boolean = true) =>
  api.useQuery("get", "/research/reports/", { params: { query } }, { enabled, throwOnError: false });

export const researchReportOptions = (reportId: string) =>
  api.queryOptions("get", "/research/reports/{report_id}", { params: { path: { report_id: reportId } } });

export const useResearchReportSuspense = (reportId: string) => useSuspenseQuery(researchReportOptions(reportId));

export const useCancelResearch = () =>
  api.useMutation("post", "/research/sessions/{session_id}:cancel", {
    onSuccess: invalidateResearchSessions
  });
